import { useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router";
import {
  CreateEnvironmentUrlUpdates,
  getCreateEnvironmentSearch,
} from "./createEnvironmentUrlState";

export interface CreateEnvironmentHistoryState {
  fromCreateEnvironmentComputePicker?: boolean;
  [key: string]: unknown;
}

const clearSelectedCompute: CreateEnvironmentUrlUpdates = {
  gpu: undefined,
  instanceType: undefined,
  storage: undefined,
  location: undefined,
  customRegion: undefined,
};

export const useCreateEnvironmentNavigation = () => {
  const history = useHistory<CreateEnvironmentHistoryState>();
  const routerLocation = useLocation<CreateEnvironmentHistoryState>();
  const pendingPickerSearch = useRef<string>();
  // Browser POP restores the historical form state. The selected-card Edit
  // action stages its own POP, so keep its current software selection intact.
  const shouldRestoreSoftwareFromUrl =
    history.action === "POP" && pendingPickerSearch.current === undefined;

  useEffect(() => {
    if (pendingPickerSearch.current === undefined) {
      return;
    }

    const nextSearch = pendingPickerSearch.current;
    pendingPickerSearch.current = undefined;
    history.replace({
      pathname: routerLocation.pathname,
      search: nextSearch,
      state: routerLocation.state,
    });
  }, [
    history,
    routerLocation.key,
    routerLocation.pathname,
    routerLocation.search,
    routerLocation.state,
  ]);

  const replaceCreateEnvironmentSearch = useCallback(
    (updates: CreateEnvironmentUrlUpdates) => {
      const nextSearch = getCreateEnvironmentSearch(
        routerLocation.search,
        updates,
      );
      history.replace({
        pathname: routerLocation.pathname,
        search: nextSearch,
        state: routerLocation.state,
      });
    },
    [
      history,
      routerLocation.pathname,
      routerLocation.search,
      routerLocation.state,
    ],
  );

  const pushCreateEnvironmentSearch = useCallback(
    (updates: CreateEnvironmentUrlUpdates) => {
      const nextSearch = getCreateEnvironmentSearch(
        routerLocation.search,
        updates,
      );
      history.push({
        pathname: routerLocation.pathname,
        search: nextSearch,
        state: {
          ...routerLocation.state,
          fromCreateEnvironmentComputePicker: true,
        },
      });
    },
    [
      history,
      routerLocation.pathname,
      routerLocation.search,
      routerLocation.state,
    ],
  );

  const returnToComputePicker = useCallback(() => {
    if (routerLocation.state?.fromCreateEnvironmentComputePicker) {
      pendingPickerSearch.current = getCreateEnvironmentSearch(
        routerLocation.search,
        clearSelectedCompute,
      );
      history.goBack();
      return;
    }

    replaceCreateEnvironmentSearch(clearSelectedCompute);
  }, [
    history,
    replaceCreateEnvironmentSearch,
    routerLocation.search,
    routerLocation.state,
  ]);

  return {
    routerLocation,
    replaceCreateEnvironmentSearch,
    pushCreateEnvironmentSearch,
    returnToComputePicker,
    shouldRestoreSoftwareFromUrl,
  };
};
